/**
 * The skills catalog panel in a focus area's workspace.
 *
 * Once the catalog has categories it is one list for everyone, shown grouped
 * by them: each category with its skills and what each covers. Before that
 * (a server without categories), it is this focus area's slice (#1349), with
 * entries moved between focus areas or to the shared pool.
 *
 * Who may act is the SERVER's answer — every entry carries `can_curate`, the
 * same rule PATCH enforces; admins only, since the categories. Only the add
 * row has no entry to ask, so it is gated on the same rule locally via
 * useEffectiveUser; the backend refuses regardless.
 *
 * Merge and delete are deliberately absent — they must repoint talents'
 * skills and rewrite demands, and that tooling is #1346.
 */

import { useState } from 'react'
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Select,
  Spinner,
  Switch,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  Add16Regular,
  Checkmark16Regular,
  Dismiss16Regular,
  FolderRegular,
  History16Regular,
} from '@fluentui/react-icons'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import {
  useCapabilities,
  useCapabilityHistory,
  useCapabilityUsage,
  useCreateCapability,
  useUpdateCapability,
} from '../hooks/useCapabilities'
import { useToast } from './ToastProvider'
import { apiErrorMessage } from '../lib/apiError'
import { formatRelative } from '../lib/relativeTime'
import { groupCatalog, hasCategories, searchCatalog } from '../lib/skillCatalog'
import { skillKey } from '../lib/skillMatch'
import { FOCUS_AREAS } from '../types/focus_area'
import type { Capability, CapabilityHistoryEntry } from '../types/capability'
import { Link as RouterLink } from 'react-router-dom'
import { bayerColors } from '../theme'

const CROSS_FA = ''
// Plain word over jargon: a skill nobody's focus area owns is shared.
const CROSS_FA_LABEL = 'Shared'

const useStyles = makeStyles({
  section: {
    background: '#FFFFFF',
    border: '1px solid #e6e6e6',
    borderRadius: '6px',
    padding: '16px',
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '12px',
  },
  title: {
    fontSize: '14px',
    fontWeight: 600,
    color: bayerColors.navy,
    margin: '0 0 4px',
  },
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    padding: '4px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  name: {
    flex: '1 1 200px',
    fontSize: '13px',
    minWidth: '160px',
  },
  nameInput: {
    flex: '1 1 200px',
    minWidth: '160px',
  },
  usage: {
    fontSize: '12px',
    color: '#757575',
    minWidth: '72px',
  },
  addRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  addRowNext: {
    marginTop: '6px',
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  search: {
    flexGrow: 1,
  },
  count: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
  },
  dupe: {
    fontSize: '11px',
    color: '#7a5f00',
    background: '#fffbe6',
    border: '1px solid #f0e68c',
    borderRadius: '4px',
    padding: '5px 8px',
    margin: '6px 0 0',
  },
  why: {
    fontFamily: 'inherit',
    fontSize: '12px',
    color: bayerColors.navyMid,
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textDecorationLine: 'underline',
  },
  moveBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  moveLabel: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    marginBottom: '4px',
  },
  moveWarn: {
    fontSize: '12px',
    color: '#5c4a10',
    background: '#fdf9ef',
    border: '1px solid #f0d9a0',
    borderRadius: '4px',
    padding: '9px 11px',
    lineHeight: 1.45,
  },
  moveNote: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  hint: {
    fontSize: '12px',
    // Was #9e9e9e: 2.7:1 on white, under the 4.5:1 floor. Fluent's own
    // secondary-text token rather than another hand-picked hex, so it moves
    // with the theme instead of needing to be re-checked.
    color: tokens.colorNeutralForeground3,
    margin: '6px 0 0',
  },
  usageBtn: {
    fontFamily: 'inherit',
    fontSize: '12px',
    color: bayerColors.navyMid,
    background: 'transparent',
    border: 'none',
    padding: '2px 4px',
    borderRadius: '4px',
    cursor: 'pointer',
    minWidth: '72px',
    textAlign: 'left',
    textDecorationLine: 'underline',
    textDecorationStyle: 'dotted',
    ':hover': { background: bayerColors.navyLight },
  },
  usageBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  usageScroll: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    maxHeight: '220px',
    overflowY: 'auto',
    paddingRight: '4px',
  },
  usageGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginBottom: '6px',
  },
  usageHead: {
    fontSize: '11px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  },
  usageItem: {
    fontSize: '12px',
    color: '#1f1f1f',
  },
  usageInactive: {
    color: tokens.colorNeutralForeground3,
  },
  usageNone: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  usageError: {
    fontSize: '12px',
    color: tokens.colorPaletteRedForeground1,
  },
  usageLink: {
    fontSize: '12px',
    color: bayerColors.navyMid,
    textDecorationLine: 'none',
    ':hover': { textDecorationLine: 'underline' },
  },
  usageStage: {
    color: tokens.colorNeutralForeground3,
  },
  renameCost: {
    flexBasis: '100%',
    fontSize: '11px',
    color: '#8a6d00',
    background: '#fffbe6',
    border: '1px solid #f0e68c',
    borderRadius: '4px',
    padding: '4px 8px',
  },
  empty: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  history: {
    flexBasis: '100%',
    padding: '4px 0 4px 12px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    borderLeft: `2px solid ${tokens.colorNeutralStroke2}`,
  },
  // By category: a section per category, its skills indented under it.
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '10px',
  },
  categoryName: {
    flex: '1 1 200px',
    minWidth: '160px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: bayerColors.navy,
  },
  memberRow: {
    paddingLeft: '22px',
  },
  nameBlock: {
    flex: '1 1 200px',
    minWidth: '160px',
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    fontSize: '13px',
  },
  desc: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  groupHead: {
    fontSize: '11px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  },
})

function homeLabel(code: string | null | undefined): string {
  return code ? code : CROSS_FA_LABEL
}

function describeChange(e: CapabilityHistoryEntry): string {
  // A name row with no `old_value` is the entry being created — the same
  // shape the backend writes for a create, so "No changes recorded" means
  // one thing only: nothing has happened since.
  if (e.field_name === 'name' && !e.old_value) return `Created “${e.new_value ?? ''}”`
  if (e.field_name === 'name') return `Renamed “${e.old_value ?? ''}” → “${e.new_value ?? ''}”`
  if (e.field_name === 'focus_area_code')
    return `Moved ${homeLabel(e.old_value)} → ${homeLabel(e.new_value)}`
  // Written by the merge tooling (#1346), which runs outside the app: the
  // entry absorbed another one, and the name that disappeared is worth
  // keeping — somebody will search for it.
  if (e.field_name === 'merged_from') return `Merged “${e.old_value ?? ''}” into this skill`
  return `${e.field_name}: ${e.old_value ?? '—'} → ${e.new_value ?? '—'}`
}

function SkillHistory({ id }: { id: number }) {
  const styles = useStyles()
  const { data, isLoading, isError, error, refetch } = useCapabilityHistory(id)
  if (isLoading) return <div className={styles.history}>Loading history…</div>
  // A failed request is an empty list too, and empty reads as "never changed".
  if (isError && !data)
    return (
      <div className={styles.history} role="alert" data-testid={`fa-skill-history-list-${id}`}>
        {apiErrorMessage(error, 'Couldn’t load the history.')}{' '}
        <button type="button" className={styles.why} onClick={() => void refetch()}>
          Try again
        </button>
      </div>
    )
  const entries = data ?? []
  return (
    <div className={styles.history} data-testid={`fa-skill-history-list-${id}`}>
      {entries.length === 0
        ? 'No changes recorded.'
        : entries.map((e) => (
            <div key={e.id}>
              {describeChange(e)}
              {e.changed_by_name ? ` · ${e.changed_by_name}` : ''}
              {e.changed_at ? ` · ${formatRelative(e.changed_at)}` : ''}
            </div>
          ))}
    </div>
  )
}

/**
 * The rows behind the two counts, in a dialog. It was an inline disclosure
 * like the history; the owner's point against that was scale - a shared skill
 * such as Python will one day list dozens of talents and demands, and a row
 * that grows to a screenful pushes the rest of the catalog out of sight. A
 * dialog scrolls on its own and leaves the list where it was.
 *
 * Talents link out to the directory filtered on this skill; demands link to
 * themselves.
 */
function SkillUsageDialog({ c, onClose }: { c: Capability; onClose: () => void }) {
  const styles = useStyles()
  const { data, isLoading, isError, error } = useCapabilityUsage(c.id)
  const talents = data?.talents ?? []
  const demands = data?.demands ?? []
  // The list is council-only, while the counts on the row are not. A refused
  // or failed request used to render "0 talents · none", contradicting the row
  // that opened it — so it says why instead.
  const failed = isError && !data
  return (
    <Dialog open modalType="modal" onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface style={{ maxWidth: 560 }}>
        <DialogBody>
          <DialogTitle>Who uses “{c.name}”</DialogTitle>
          <DialogContent>
            <div className={styles.usageBody} data-testid={`fa-skill-usage-list-${c.id}`}>
              {isLoading ? (
                <Spinner size="tiny" label="Loading…" />
              ) : failed ? (
                <span className={styles.usageError} role="alert">
                  {apiErrorMessage(error, 'Couldn’t load who uses this skill.')}
                </span>
              ) : (
                <>
                  <div className={styles.usageGroup}>
                    <span className={styles.usageHead}>
                      {talents.length} talent{talents.length === 1 ? '' : 's'}
                    </span>
                    {talents.length === 0 ? (
                      <span className={styles.usageNone}>none</span>
                    ) : (
                      <div className={styles.usageScroll}>
                        {talents.map((t) => (
                          <span key={t.id} className={styles.usageItem}>
                            {t.name ?? `#${t.id}`}
                            {!t.is_active && (
                              <span className={styles.usageInactive}> (inactive)</span>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                    {talents.length > 0 && (
                      <RouterLink
                        className={styles.usageLink}
                        to={`/talents?skill=${encodeURIComponent(c.name)}`}
                        data-testid={`fa-skill-usage-directory-${c.id}`}
                        onClick={onClose}
                      >
                        Open in Talent directory →
                      </RouterLink>
                    )}
                  </div>
                  <div className={styles.usageGroup}>
                    <span className={styles.usageHead}>
                      {demands.length} demand{demands.length === 1 ? '' : 's'}
                    </span>
                    {demands.length === 0 ? (
                      <span className={styles.usageNone}>none</span>
                    ) : (
                      <div className={styles.usageScroll}>
                        {demands.map((d) => (
                          <RouterLink
                            key={d.id}
                            className={styles.usageLink}
                            to={`/demand/${d.id}`}
                            onClick={onClose}
                          >
                            {d.demand_id ? `${d.demand_id} · ` : ''}
                            {d.title}
                            <span className={styles.usageStage}> · {d.stage}</span>
                          </RouterLink>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} data-testid="fa-skill-usage-close">
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

function demandUse(c: Capability): number {
  return c.demand_usage_count ?? 0
}

/**
 * How far a rename reaches, shown while the new name is being typed — the
 * moment of the decision, and one row at a time rather than on every row.
 *
 * Talents follow a rename by foreign key. Demands did not: `demands.skills`
 * holds strings, so every demand naming the entry used to keep the old word,
 * drop off the catalog, and be reported by the nightly drift guard as if the
 * vocabulary had leaked. The rename repoints them in its own transaction now,
 * which is why this reads as reach rather than as a warning — but it is still
 * a write to demands the curator does not own, so it is stated first.
 */
function RenameReach({ c }: { c: Capability }) {
  const styles = useStyles()
  const n = demandUse(c)
  if (n === 0) return null
  return (
    <span className={styles.renameCost} data-testid={`fa-skill-rename-cost-${c.id}`}>
      {n} demand{n === 1 ? '' : 's'} ask{n === 1 ? 's' : ''} for “{c.name}” — renaming
      updates {n === 1 ? 'it' : 'them'} too.
    </span>
  )
}

export function FaSkillCatalogManager({
  code,
  hideTitle = false,
}: {
  code: string
  /** The workspace folds this panel under its own header; no second title. */
  hideTitle?: boolean
}) {
  const styles = useStyles()
  const toast = useToast()
  const user = useEffectiveUser()
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'
  // The add row has no entry to carry the server's answer, so it uses the
  // same rule locally: admins only. The backend enforces it either way.
  const canAdd = isAdmin
  // History is a read, not curation: any council member may open it, the
  // rule its endpoint applies, whoever may change the entry.
  const canReadHistory = isAdmin || user?.role === 'council'

  const [showCrossFa, setShowCrossFa] = useState(false)
  // The whole catalog decides the view: with categories, all of it grouped by
  // them; without, this focus area's slice as before. Unread, there is no
  // telling which view applies, so its failure is the panel's failure.
  const whole = useCapabilities()
  const slice = useCapabilities(code, showCrossFa)
  const grouped = hasCategories(whole.data ?? [])
  const data = grouped ? whole.data : slice.data
  const isLoading = whole.isLoading || (!grouped && slice.isLoading)
  const wholeFailed = whole.isError && !whole.data
  const loadFailed = wholeFailed || (!grouped && slice.isError && !slice.data)
  const loadError = wholeFailed ? whole.error : slice.error
  // The add row's shape follows the answer, so it waits for one: handed the
  // flat form too early, an admin would send a skill the categorised server
  // refuses for having no category.
  const catalogKnown = !isLoading && !loadFailed
  const create = useCreateCapability()
  const update = useUpdateCapability()

  const [newName, setNewName] = useState('')
  const [newHome, setNewHome] = useState<string>(code)
  // With categories, a new skill goes in one; what it covers comes with it.
  const [newCategory, setNewCategory] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newExamples, setNewExamples] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [draft, setDraft] = useState('')
  const [historyId, setHistoryId] = useState<number | null>(null)
  const [usageId, setUsageId] = useState<number | null>(null)
  const [query, setQuery] = useState('')
  // The entry whose home is being changed, and where to. `null` = no dialog.
  // A move used to happen on a <Select onChange>, so a scroll over a focused
  // control was a write — and the dangerous direction is the irreversible one,
  // which is exactly when the Undo toast cannot help (authorisation follows the
  // entry's CURRENT home, so giving it away gives away the right to take it
  // back). Two deliberate steps instead, with the cost stated in between.
  const [moving, setMoving] = useState<Capability | null>(null)
  const [moveTarget, setMoveTarget] = useState<string>(CROSS_FA)

  function onError(err: unknown, fallback: string) {
    toast.showToast({ title: apiErrorMessage(err, fallback), intent: 'error' })
  }

  function handleAdd() {
    const name = newName.trim()
    if (!name) return
    if (grouped) {
      if (!newCategory) return
      // A skill in a category belongs to no focus area: shared. The category
      // stays picked, for the next one.
      create.mutate(
        {
          name,
          focus_area_code: null,
          parent_id: Number(newCategory),
          description: newDescription.trim() || undefined,
          examples: newExamples.trim() || undefined,
        },
        {
          onSuccess: () => {
            setNewName('')
            setNewDescription('')
            setNewExamples('')
          },
          onError: (err) => onError(err, 'Could not add the skill'),
        },
      )
      return
    }
    create.mutate(
      { name, focus_area_code: newHome === CROSS_FA ? null : newHome },
      {
        onSuccess: () => setNewName(''),
        onError: (err) => onError(err, 'Could not add the skill'),
      },
    )
  }

  function startRename(c: Capability) {
    setEditingId(c.id)
    setDraft(c.name)
  }

  function saveRename(c: Capability) {
    const name = draft.trim()
    if (!name || name === c.name) {
      setEditingId(null)
      return
    }
    const previousName = c.name
    update.mutate(
      { id: c.id, body: { name } },
      {
        onSuccess: (updated) => {
          setEditingId(null)
          // Undo rather than a confirmation — the decision on #1245, which
          // ToastProvider records: a prompt before every rename taxes the
          // correct ones to catch the rare wrong one. It earns its place more
          // here than on a chip, because a rename also rewrites demands.
          //
          // Undoing a rename is just the rename backwards, repointing
          // included, so the demands follow it home.
          const moved = updated?.demands_repointed ?? 0
          toast.showToast({
            title: `Renamed to “${updated.name}”`,
            body: moved > 0 ? `${moved} demand${moved === 1 ? '' : 's'} updated too.` : undefined,
            intent: 'success',
            action: {
              label: 'Undo',
              onClick: () =>
                update.mutate(
                  { id: c.id, body: { name: previousName } },
                  { onError: (err) => onError(err, 'Could not undo the rename') },
                ),
            },
          })
        },
        onError: (err) => onError(err, 'Could not rename the skill'),
      },
    )
  }

  function applyMove(c: Capability, value: string) {
    const target = value === CROSS_FA ? null : value
    const previousHome = c.focus_area_code ?? null
    if (target === previousHome) return
    setMoving(null)
    update.mutate(
      { id: c.id, body: { focus_area_code: target } },
      {
        onSuccess: (updated) => {
          //
          // The offer of an Undo depends on the SERVER's answer, not on the
          // role: authorisation is on the entry's CURRENT home, so a council
          // giving an entry to another focus area gives away the right to take
          // it back, and the button would only 403. `can_curate` is that
          // answer, recomputed for this caller after the move.
          const reversible = !!updated?.can_curate
          toast.showToast({
            title: `“${c.name}” moved to ${homeLabel(target)}`,
            body: reversible
              ? undefined
              : `It belongs to ${homeLabel(target)} now, so you can no longer change it.`,
            intent: reversible ? 'success' : 'warning',
            ...(reversible
              ? {
                  action: {
                    label: 'Undo',
                    onClick: () =>
                      update.mutate(
                        { id: c.id, body: { focus_area_code: previousHome } },
                        { onError: (err) => onError(err, 'Could not undo the move') },
                      ),
                  },
                }
              : {}),
          })
        },
        onError: (err) => onError(err, 'Could not move the skill'),
      },
    )
  }

  const list = data ?? []
  const q = query.trim().toLowerCase()
  const shown = q ? list.filter((c) => c.name.toLowerCase().includes(q)) : list
  // By category, searched as the pickers search: name, description, examples
  // and category. Counted in skills, not entries — a category is a heading.
  const groups = grouped ? searchCatalog(groupCatalog(list), query) : []
  const categories = grouped
    ? list.filter((c) => c.is_category).sort((a, b) => a.name.localeCompare(b.name))
    : []
  const total = grouped ? list.filter((c) => !c.is_category).length : list.length
  const shownCount = grouped ? groups.reduce((n, g) => n + g.skills.length, 0) : shown.length
  const nothingShown = grouped ? groups.length === 0 : shown.length === 0

  // "Is this word already in here?" was answered by a refusal AFTER pressing
  // Add. It is answered while typing now - but only as a hint: without
  // categories `list` holds this focus area's rows (plus the shared ones when
  // the switch is on), so a duplicate living in ANOTHER focus area is
  // invisible here and the server's 409 is still the real guard.
  const newKey = skillKey(newName)
  const duplicate = newKey ? list.find((c) => skillKey(c.name) === newKey) : undefined
  const duplicateWhere = !duplicate
    ? ''
    : duplicate.is_category
      ? ', as a category'
      : duplicate.category
        ? `, in ${duplicate.category}`
        : duplicate.focus_area_code
          ? `, homed in ${duplicate.focus_area_code}`
          : ', shared across focus areas'

  // Whether the mover could undo it. An admin curates anywhere; a council also
  // curates the cross-FA entries (#1349), so a move to Cross-FA is reversible
  // while a move to another focus area is not.
  const moveReversible = isAdmin || moveTarget === CROSS_FA

  function openMove(c: Capability) {
    setMoving(c)
    setMoveTarget(c.focus_area_code ?? CROSS_FA)
  }

  function retryLoad() {
    void whole.refetch()
    if (!grouped) void slice.refetch()
  }

  /**
   * One entry. `legacy` is a row outside the categories, as it always was;
   * `category` heads its section; `skill` sits under it with what it covers.
   * Focus areas own no categorised entry, so those have no home and no Move.
   */
  function renderRow(c: Capability, kind: 'legacy' | 'category' | 'skill') {
    const editing = editingId === c.id
    const curate = !!c.can_curate
    return (
      <div
        key={c.id}
        className={mergeClasses(styles.row, kind === 'skill' && styles.memberRow)}
        data-testid={`fa-skill-${c.id}`}
      >
        {editing ? (
          <Input
            className={styles.nameInput}
            value={draft}
            onChange={(_, d) => setDraft(d.value)}
            aria-label={`New name for ${c.name}`}
            data-testid={`fa-skill-rename-input-${c.id}`}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveRename(c)
              if (e.key === 'Escape') setEditingId(null)
            }}
          />
        ) : kind === 'category' ? (
          <span className={styles.categoryName}>
            <FolderRegular aria-hidden />
            {c.name}
          </span>
        ) : kind === 'skill' ? (
          <span
            className={styles.nameBlock}
            title={c.examples ? `Examples: ${c.examples}` : undefined}
          >
            <span>{c.name}</span>
            {c.description && <span className={styles.desc}>{c.description}</span>}
          </span>
        ) : (
          <span className={styles.name}>{c.name}</span>
        )}

        {kind === 'legacy' && (
          <Badge
            appearance="tint"
            color="informative"
            size="medium"
            data-testid={`fa-skill-home-${c.id}`}
          >
            {homeLabel(c.focus_area_code)}
          </Badge>
        )}

        <button
          type="button"
          className={styles.usageBtn}
          data-testid={`fa-skill-usage-${c.id}`}
          aria-haspopup="dialog"
          title="Who uses this skill"
          onClick={() => setUsageId(c.id)}
        >
          {c.usage_count ?? 0} {c.usage_count === 1 ? 'talent' : 'talents'}
          {' · '}
          {demandUse(c)} {demandUse(c) === 1 ? 'demand' : 'demands'}
        </button>

        {curate && editing && <RenameReach c={c} />}
        {curate && editing && (
          <>
            <Tooltip content="Save name" relationship="label">
              <Button
                appearance="subtle"
                size="small"
                icon={<Checkmark16Regular />}
                aria-label={`Save name for ${c.name}`}
                data-testid={`fa-skill-rename-save-${c.id}`}
                disabled={update.isPending}
                onClick={() => saveRename(c)}
              />
            </Tooltip>
            <Tooltip content="Cancel" relationship="label">
              <Button
                appearance="subtle"
                size="small"
                icon={<Dismiss16Regular />}
                aria-label="Cancel rename"
                onClick={() => setEditingId(null)}
              />
            </Tooltip>
          </>
        )}
        {curate && !editing && (
          <>
            <Button
              appearance="subtle"
              size="small"
              aria-label={`Rename ${c.name}`}
              data-testid={`fa-skill-rename-${c.id}`}
              onClick={() => startRename(c)}
            >
              Rename
            </Button>
            {kind === 'legacy' && (
              <Button
                appearance="subtle"
                size="small"
                aria-label={`Move ${c.name} to another focus area`}
                data-testid={`fa-skill-move-${c.id}`}
                onClick={() => openMove(c)}
              >
                Move…
              </Button>
            )}
          </>
        )}
        {canReadHistory && (
          <Tooltip content="Change history" relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<History16Regular />}
              aria-label={`History of ${c.name}`}
              aria-pressed={historyId === c.id}
              data-testid={`fa-skill-history-${c.id}`}
              onClick={() => setHistoryId(historyId === c.id ? null : c.id)}
            />
          </Tooltip>
        )}
        {historyId === c.id && <SkillHistory id={c.id} />}
      </div>
    )
  }

  return (
    <section className={styles.section} data-testid="fa-skill-catalog">
      <div className={styles.head}>
        <div>
          {!hideTitle && <h3 className={styles.title}>Skills catalog</h3>}
        </div>
        {!grouped && (
          <Switch
            label="Show shared skills"
            checked={showCrossFa}
            onChange={(_, d) => setShowCrossFa(!!d.checked)}
            data-testid="fa-skill-show-cross-fa"
          />
        )}
      </div>

      {list.length > 0 && (
        <div className={styles.searchRow}>
          <Input
            className={styles.search}
            value={query}
            onChange={(_, d) => setQuery(d.value)}
            placeholder="Search this catalog…"
            aria-label="Search the skills catalog"
            data-testid="fa-skill-search"
          />
          <span className={styles.count} data-testid="fa-skill-count">
            {q ? `${shownCount} of ${total} shown` : `${total} skills`}
          </span>
        </div>
      )}

      {/* A failed load is an empty list too, which read as an empty catalog. */}
      {isLoading ? (
        <Spinner size="tiny" />
      ) : loadFailed ? (
        <div className={styles.usageError} role="alert" data-testid="fa-skill-load-error">
          {apiErrorMessage(loadError, 'Couldn’t load the skills catalog.')}{' '}
          <button
            type="button"
            className={styles.why}
            onClick={retryLoad}
            data-testid="fa-skill-load-retry"
          >
            Try again
          </button>
        </div>
      ) : list.length === 0 ? (
        <div className={styles.empty}>
          No skills homed in {code} yet{canAdd ? ' — add one below.' : '.'}
        </div>
      ) : nothingShown ? (
        <div className={styles.empty} data-testid="fa-skill-no-match">
          Nothing here matches “{query.trim()}”.{canAdd ? ' Add it below, or clear the search.' : ''}
        </div>
      ) : grouped ? (
        <div className={styles.rows}>
          {groups.map((g) => (
            <div
              key={g.category ? g.category.id : 'rest'}
              className={styles.group}
              data-testid={g.category ? `fa-skill-group-${g.category.id}` : 'fa-skill-group-other'}
            >
              {g.category ? (
                renderRow(g.category, 'category')
              ) : (
                <span className={styles.groupHead}>Other</span>
              )}
              {g.skills.map((s) => renderRow(s, g.category ? 'skill' : 'legacy'))}
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.rows}>{shown.map((c) => renderRow(c, 'legacy'))}</div>
      )}

      {!catalogKnown ? null : canAdd ? (
        <>
          <div className={styles.addRow}>
            <Input
              className={styles.nameInput}
              value={newName}
              onChange={(_, d) => setNewName(d.value)}
              placeholder="New skill…"
              aria-label="New skill name"
              data-testid="fa-skill-new-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd()
              }}
            />
            {grouped ? (
              <Select
                value={newCategory}
                aria-label="Category for the new skill"
                data-testid="fa-skill-new-category"
                onChange={(_, d) => setNewCategory(d.value)}
              >
                <option value="">Category…</option>
                {categories.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name}
                  </option>
                ))}
              </Select>
            ) : (
              <Select
                value={newHome}
                aria-label="Home for the new skill"
                data-testid="fa-skill-new-home"
                onChange={(_, d) => setNewHome(d.value)}
              >
                <option value={code}>{code}</option>
                <option value={CROSS_FA}>{CROSS_FA_LABEL}</option>
              </Select>
            )}
            <Button
              appearance="primary"
              icon={<Add16Regular />}
              onClick={handleAdd}
              disabled={
                !newName.trim() || (grouped && !newCategory) || !!duplicate || create.isPending
              }
              data-testid="fa-skill-add-btn"
            >
              Add
            </Button>
          </div>
          {grouped && (
            <div className={mergeClasses(styles.addRow, styles.addRowNext)}>
              <Input
                className={styles.nameInput}
                value={newDescription}
                onChange={(_, d) => setNewDescription(d.value)}
                placeholder="What it covers"
                aria-label="What the new skill covers"
                data-testid="fa-skill-new-description"
              />
              <Input
                className={styles.nameInput}
                value={newExamples}
                onChange={(_, d) => setNewExamples(d.value)}
                placeholder="Examples"
                aria-label="Examples for the new skill"
                data-testid="fa-skill-new-examples"
              />
            </div>
          )}
          {duplicate ? (
            <p className={styles.dupe} data-testid="fa-skill-duplicate">
              Already in the catalog{duplicateWhere}. Rename that one instead.
            </p>
          ) : (
            <p className={styles.hint}>
              {grouped
                ? 'Pick its category and say what it covers.'
                : 'Short names, like “dbt” or “Multi-omics”.'}{' '}
              <Tooltip
                content="Merging two entries has to repoint every talent and rewrite every demand that names them, so it is not built yet (#1346). Delete is absent for the same reason."
                relationship="description"
              >
                <button type="button" className={styles.why} data-testid="fa-skill-no-merge">
                  No merge or delete yet
                </button>
              </Tooltip>
              .
            </p>
          )}
        </>
      ) : (
        <p className={styles.hint} data-testid="fa-skill-report-missing">
          Missing a skill? Report it through Help and feedback.
        </p>
      )}

      {usageId !== null &&
        (() => {
          const c = list.find((x) => x.id === usageId)
          return c ? <SkillUsageDialog c={c} onClose={() => setUsageId(null)} /> : null
        })()}

      {moving && (
        <Dialog open modalType="modal" onOpenChange={(_, d) => !d.open && setMoving(null)}>
          <DialogSurface style={{ maxWidth: 480 }}>
            <DialogBody>
              <DialogTitle>Move “{moving.name}” to another focus area</DialogTitle>
              <DialogContent>
                <div className={styles.moveBody}>
                  <div>
                    <label className={styles.moveLabel} htmlFor="fa-skill-move-target">
                      New home
                    </label>
                    <Select
                      id="fa-skill-move-target"
                      value={moveTarget}
                      data-testid="fa-skill-move-target"
                      onChange={(_, d) => setMoveTarget(d.value)}
                    >
                      {FOCUS_AREAS.map((fa) => (
                        <option key={fa.code} value={fa.code}>
                          {fa.code} · {fa.name}
                          {(moving.focus_area_code ?? CROSS_FA) === fa.code ? ' (now)' : ''}
                        </option>
                      ))}
                      <option value={CROSS_FA}>
                        {CROSS_FA_LABEL}
                        {(moving.focus_area_code ?? CROSS_FA) === CROSS_FA ? ' (now)' : ''}
                      </option>
                    </Select>
                  </div>

                  {/* The consequence, before the write rather than in a toast
                      after it.
                      Authorisation follows the entry's CURRENT home, so handing
                      it to another FOCUS AREA hands over the right to hand it
                      back. Two cases keep it: an admin curates anywhere, and
                      #1349's rule gives every council the cross-FA entries - so
                      moving something to Cross-FA is reversible, and the
                      warning must not claim otherwise. */}
                  <div className={styles.moveWarn} data-testid="fa-skill-move-warning">
                    {moveReversible
                      ? `${homeLabel(moveTarget === CROSS_FA ? null : moveTarget)} owns it after this. You can still move it back.`
                      : `${homeLabel(moveTarget === CROSS_FA ? null : moveTarget)} owns it after this — you will not be able to move it back. Ask ${homeLabel(moveTarget === CROSS_FA ? null : moveTarget)} or an admin.`}
                  </div>

                  <span className={styles.moveNote}>
                    Nothing else changes: {moving.usage_count ?? 0}{' '}
                    {moving.usage_count === 1 ? 'talent' : 'talents'} and {demandUse(moving)}{' '}
                    {demandUse(moving) === 1 ? 'demand' : 'demands'} keep it.
                  </span>
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  appearance="secondary"
                  onClick={() => setMoving(null)}
                  data-testid="fa-skill-move-cancel"
                >
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  disabled={
                    update.isPending || moveTarget === (moving.focus_area_code ?? CROSS_FA)
                  }
                  onClick={() => applyMove(moving, moveTarget)}
                  data-testid="fa-skill-move-confirm"
                >
                  Move to {homeLabel(moveTarget === CROSS_FA ? null : moveTarget)}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </section>
  )
}
